import type { DocumentEncoding } from "./encodingLabel.js";
import { detectEol, convertEol, type EolStyle } from "./eol.js";
import type { LanguageMode } from "./languageMode.js";
import { inferLanguageModeFromFilename } from "./inferLanguageFromFilename.js";
import { isImageViewFile } from "../io/imageViewFile.js";
import { shouldTreatFileAsPdf } from "../io/pdfViewFile.js";
import { emptyWorkbookJson, nppJsonStringToXlsxBuffer, parseNppExcelDocOrNull } from "../io/excelWorkbook.js";
import {
  openEditorFile,
  readFileAsText,
  saveExcelWithPicker,
  saveWithPicker,
  writeBinaryToExistingHandle,
  writeToExistingHandle,
} from "../io/nativeFile.js";
import {
  clearRecentFileHandles,
  getRecentFileHandle,
  persistRecentFileHandle,
} from "../io/recentFileHandles.js";
import type { DiffEditorSnapshot } from "../ui/diffTabMonaco.js";
import type { TabColorId } from "./tabColors.js";
import { nextTabColor } from "./tabColors.js";
import { tabTitleFromFetchedPageUrl } from "../io/fetchHtmlPage.js";

export type MergeTextTabsByColorResult =
  | { ok: true }
  | { ok: false; error: string };

const RECENT_KEY = "npp-web-recent-files";

/** Max MRU entries (localStorage + File menu). */
export const MAX_RECENT_FILES = 10;

function basenameFromDisplayPath(pathKey: string): string {
  const s = pathKey.replace(/\\/g, "/").trim();
  if (!s) return "";
  const i = s.lastIndexOf("/");
  return i >= 0 ? s.slice(i + 1) : s;
}

/** Browser `File.text()` limit for folder-workspace opens (same guard as `FolderWorkspacePane`). */
const MAX_WORKSPACE_FILE_BYTES = 15 * 1024 * 1024;

export type EditorViewMode = "text" | "image" | "pdf" | "diff" | "video";

/** One tab / buffer (Buffer.* responsibilities, PROJECT §5, §16.1). */
export type TabDocument = {
  id: string;
  title: string;
  content: string;
  savedContent: string;
  dirty: boolean;
  filePath: string | null;
  fileHandle: FileSystemFileHandle | null;
  encoding: DocumentEncoding;
  eol: EolStyle;
  history: string[];
  historyIndex: number;
  language: LanguageMode;
  /** When `image` or `pdf`, `previewObjectUrl` is a `blob:` URL for `<img>` or `<iframe>`. */
  viewMode: EditorViewMode;
  previewObjectUrl: string | null;
  /** When `viewMode` is `diff`, holds buffers for Monaco DiffEditor. */
  diffPair: DiffEditorSnapshot | null;
  /** When `viewMode` is `diff`: false = Monaco diff editor, true = two-pane split with line highlights. */
  diffSplitView: boolean;
  /** Optional group color (double-click tab to cycle). */
  tabColor: TabColorId | null;
  /** Set when the tab was created from a NullaPad share inbox message. */
  fromShare?: boolean;
  /**
   * Session restore could not reload binary preview; UI offers “Reload” to pick the file again.
   * Only `"pdf"` / `"image"` — cleared once the user reloads.
   */
  /** Session restore could not reload binary preview; UI offers "Reload". Cleared once user reloads. */
  sessionRestorePlaceholder?: "pdf" | "image";
  /**
   * `File.lastModified` after the last load from or save to `fileHandle` (ms since epoch).
   * Used to detect external edits via polling. `null` when there is no handle or time is unknown.
   */
  fileSourceMtimeMs: number | null;
  /** Polling saw a newer file on disk while the buffer had unsaved edits (prompt on activate / poll). */
  diskChangedWhileDirty: boolean;
  /**
   * Remembered edit/preview toggle for preview-capable languages (md, tex, max, html, oeis, csv).
   * `true` = source editor visible; `false` (or absent) = preview-only mode.
   * Persisted across tab switches so the mode is restored when the user returns to this tab.
   */
  showSource?: boolean;
};

export class DocumentManager {
  private docs: TabDocument[] = [];
  private activeIdx = 0;
  private idSeq = 1;
  private untitledSeq = 1;
  /** When set, new empty tabs and split parts get this group color (from title bar swatch). */
  private newTabGroupColor: TabColorId | null = null;
  /** Same handle as the folder workspace root (for MRU / tab paths via `resolve()`). */
  private workspaceRootHandle: FileSystemDirectoryHandle | null = null;

  constructor() {
    this.addEmptyTab();
  }

  /**
   * Keep in sync with the “Open Folder” workspace root so File → Open / Save As can store
   * workspace-relative paths in recent files and tab `filePath` when the picked file lies under that tree.
   */
  setWorkspaceRootHandle(root: FileSystemDirectoryHandle | null): void {
    this.workspaceRootHandle = root;
  }

  /** Display path for MRU and `filePath`: workspace-relative, `webkitRelativePath`, or basename. */
  private async displayPathForFile(file: File, handle: FileSystemFileHandle | null): Promise<string> {
    const fallback = file.name;
    if (handle && this.workspaceRootHandle) {
      try {
        const segments = await this.workspaceRootHandle.resolve(handle);
        if (segments?.length) {
          return `${this.workspaceRootHandle.name}/${segments.join("/")}`;
        }
      } catch {
        /* ignore */
      }
    }
    const wrp = file.webkitRelativePath?.trim();
    if (wrp) return wrp.replace(/\\/g, "/");
    return fallback;
  }

  /** Next id / untitled suffix for tab session JSON. */
  getTabSessionCounters(): { untitledSeq: number; idSeq: number } {
    return { untitledSeq: this.untitledSeq, idSeq: this.idSeq };
  }

  /**
   * Replace all tabs from `localStorage` restore. Revokes blob previews on previous buffers.
   * `fileHandle` is always cleared (not serializable). If `tabs` is empty, one empty tab is created.
   */
  restoreWorkspaceFromPersisted(
    tabs: TabDocument[],
    activeIndex: number,
    untitledSeq: number,
    idSeq: number,
  ): void {
    for (const d of this.docs) {
      this.revokePreviewIfAny(d);
    }
    if (tabs.length === 0) {
      this.docs = [];
      this.untitledSeq = 1;
      this.idSeq = 1;
      this.addEmptyTab();
      this.activeIdx = 0;
      return;
    }
    this.docs = tabs;
    this.activeIdx = Math.max(0, Math.min(activeIndex, tabs.length - 1));

    let maxDocNum = 0;
    for (const t of tabs) {
      const m = /^doc-(\d+)$/.exec(t.id);
      if (m) maxDocNum = Math.max(maxDocNum, parseInt(m[1]!, 10));
    }
    this.idSeq = Math.max(idSeq, maxDocNum + 1, 1);

    let maxUntitled = 0;
    const ure = /^Untitled-(\d+)$/;
    for (const t of tabs) {
      const m = ure.exec(t.title);
      if (m) maxUntitled = Math.max(maxUntitled, parseInt(m[1]!, 10));
    }
    this.untitledSeq = Math.max(untitledSeq, maxUntitled + 1, 1);
  }

  get documents(): readonly TabDocument[] {
    return this.docs;
  }

  get activeIndex(): number {
    return this.activeIdx;
  }

  get active(): TabDocument | null {
    return this.docs[this.activeIdx] ?? null;
  }

  private newId(): string {
    return `doc-${this.idSeq++}`;
  }

  private createTab(
    title: string,
    content: string,
    filePath: string | null,
    fileHandle: FileSystemFileHandle | null,
  ): TabDocument {
    const eol = detectEol(content);
    let language = inferLanguageModeFromFilename(filePath ?? title);
    // PDF tabs must use createPdfTab (viewMode "pdf"). Never pair viewMode "text" with language "pdf"
    // — that loads binary into the textarea and breaks preview routing.
    if (language === "pdf") language = "text";
    return {
      id: this.newId(),
      title,
      content,
      savedContent: content,
      dirty: false,
      filePath,
      fileHandle,
      encoding: "utf-8",
      eol: eol,
      history: [content],
      historyIndex: 0,
      language,
      viewMode: "text",
      previewObjectUrl: null,
      diffPair: null,
      diffSplitView: false,
      tabColor: this.newTabGroupColor,
      fileSourceMtimeMs: null,
      diskChangedWhileDirty: false,
    };
  }

  private createImageTab(
    title: string,
    file: File,
    filePath: string | null,
    fileHandle: FileSystemFileHandle | null,
  ): TabDocument {
    const url = URL.createObjectURL(file);
    return {
      id: this.newId(),
      title,
      content: "",
      savedContent: "",
      dirty: false,
      filePath,
      fileHandle,
      encoding: "utf-8",
      eol: "lf",
      history: [""],
      historyIndex: 0,
      language: "text",
      viewMode: "image",
      previewObjectUrl: url,
      diffPair: null,
      diffSplitView: false,
      tabColor: this.newTabGroupColor,
      fileSourceMtimeMs: null,
      diskChangedWhileDirty: false,
    };
  }

  private createPdfTab(
    title: string,
    file: File,
    filePath: string | null,
    fileHandle: FileSystemFileHandle | null,
  ): TabDocument {
    // `getFile()` often leaves `type` empty or `application/octet-stream`; blob: URLs then open as
    // plain text in an iframe instead of the built-in PDF viewer (user sees raw %PDF source).
    const mime = file.type.trim().toLowerCase();
    const fileForBlob =
      mime === "application/pdf" || mime === "application/x-pdf"
        ? file
        : new File([file], file.name || "document.pdf", {
            type: "application/pdf",
            lastModified: file.lastModified,
          });
    const url = URL.createObjectURL(fileForBlob);
    return {
      id: this.newId(),
      title,
      content: "",
      savedContent: "",
      dirty: false,
      filePath,
      fileHandle,
      encoding: "utf-8",
      eol: "lf",
      history: [""],
      historyIndex: 0,
      language: "pdf",
      viewMode: "pdf",
      previewObjectUrl: url,
      diffPair: null,
      diffSplitView: false,
      tabColor: this.newTabGroupColor,
      fileSourceMtimeMs: null,
      diskChangedWhileDirty: false,
    };
  }

  createVideoTab(
    title: string,
    content: string,
    filePath: string | null,
    fileHandle: FileSystemFileHandle | null,
  ): TabDocument {
    return {
      id: this.newId(),
      title,
      content,
      savedContent: content,
      dirty: false,
      filePath,
      fileHandle,
      encoding: "utf-8",
      eol: "lf",
      history: [content],
      historyIndex: 0,
      language: "max",
      viewMode: "video",
      previewObjectUrl: null,
      diffPair: null,
      diffSplitView: false,
      tabColor: this.newTabGroupColor,
      fileSourceMtimeMs: null,
      diskChangedWhileDirty: false,
    };
  }

  /** Empty `.max` buffer in text mode for toolbar camera/mic hex recordings (not Nulla video view). */
  createEmptyMaxRecordingTab(): TabDocument {
    const seq = this.untitledSeq++;
    const initial = "";
    return {
      id: this.newId(),
      title: `Recording-${seq}.max`,
      content: initial,
      savedContent: initial,
      dirty: false,
      filePath: null,
      fileHandle: null,
      encoding: "utf-8",
      eol: "lf",
      history: [initial],
      historyIndex: 0,
      language: "max",
      viewMode: "text",
      previewObjectUrl: null,
      diffPair: null,
      diffSplitView: false,
      tabColor: this.newTabGroupColor,
      fileSourceMtimeMs: null,
      diskChangedWhileDirty: false,
    };
  }

  /** Open a read-only Monaco diff tab from two text snapshots. */
  addDiffTab(snapshot: DiffEditorSnapshot): void {
    const leftShort = snapshot.leftLabel.length > 32 ? `${snapshot.leftLabel.slice(0, 30)}…` : snapshot.leftLabel;
    const rightShort =
      snapshot.rightLabel.length > 32 ? `${snapshot.rightLabel.slice(0, 30)}…` : snapshot.rightLabel;
    const tab: TabDocument = {
      id: this.newId(),
      title: `Diff · ${leftShort} ↔ ${rightShort}`,
      content: "",
      savedContent: "",
      dirty: false,
      filePath: null,
      fileHandle: null,
      encoding: "utf-8",
      eol: "lf",
      history: [""],
      historyIndex: 0,
      language: "diff",
      viewMode: "diff",
      previewObjectUrl: null,
      diffPair: snapshot,
      diffSplitView: false,
      tabColor: null,
      fileSourceMtimeMs: null,
      diskChangedWhileDirty: false,
    };
    this.docs.push(tab);
    this.activeIdx = this.docs.length - 1;
  }

  /** Toggle compare tab layout (Monaco vs aligned two-pane split). */
  setDiffTabSplitView(index: number, split: boolean): void {
    const d = this.docs[index];
    if (!d || d.viewMode !== "diff") return;
    d.diffSplitView = split;
  }

  private revokePreviewIfAny(d: TabDocument): void {
    if (d.viewMode !== "text" && d.previewObjectUrl) {
      URL.revokeObjectURL(d.previewObjectUrl);
      d.previewObjectUrl = null;
    }
  }

  setActiveLanguage(mode: LanguageMode): void {
    const d = this.active;
    if (!d) return;
    d.language = mode;
    if (mode === "excel") {
      const t = d.content.trim();
      if (!t || !parseNppExcelDocOrNull(d.content)) {
        const fresh = emptyWorkbookJson();
        d.content = fresh;
        d.savedContent = fresh;
        d.dirty = false;
        d.history = [fresh];
        d.historyIndex = 0;
      }
    }
  }

  /** Title bar color filter: applied to new empty tabs and split segments until cleared. */
  setNewTabGroupColor(color: TabColorId | null): void {
    this.newTabGroupColor = color;
  }

  addEmptyTab(): void {
    const t = this.createTab(`Untitled-${this.untitledSeq++}`, "", null, null);
    this.docs.push(t);
    this.activeIdx = this.docs.length - 1;
  }

  /**
   * Open a new text tab pre-populated with content (e.g. from `git show <ref>:<path>`).
   * The tab is editable and unsaved (dirty = false, filePath = null).
   */
  addContentTab(title: string, content: string, language?: LanguageMode): void {
    const uniqueTitle = this.allocUniqueTabTitleAmongOpenTabs(title);
    const tab = this.createTab(uniqueTitle, content, null, null);
    if (language && language !== "pdf") tab.language = language;
    this.docs.push(tab);
    this.activeIdx = this.docs.length - 1;
  }

  /**
   * New text tab with fetched HTML (local editable copy; no `fileHandle` / disk path).
   * Tab title is derived from the URL; language is HTML.
   */
  addTabFromFetchedHtmlPage(sourceUrl: string, html: string): void {
    const title = tabTitleFromFetchedPageUrl(sourceUrl);
    const tab = this.createTab(title, html, null, null);
    tab.language = "html";
    this.docs.push(tab);
    this.activeIdx = this.docs.length - 1;
  }

  /**
   * Clone the tab at `index` and insert the copy immediately after it, then make it active.
   * For text/video (nulla) tabs the content is fully cloned; the copy is unbound (no file handle,
   * no filePath) and marked dirty so the user is prompted to save. Diff tabs clone their snapshot.
   * Image/PDF tabs are not supported (returns false).
   */
  duplicateTab(index: number): boolean {
    const src = this.docs[index];
    if (!src) return false;
    if (src.viewMode === "image" || src.viewMode === "pdf" || src.sessionRestorePlaceholder) {
      return false;
    }
    const title = this.allocUniqueTabTitleAmongOpenTabs(src.title);
    let clone: TabDocument;
    if (src.viewMode === "diff" && src.diffPair) {
      clone = {
        ...src,
        id: this.newId(),
        title,
        filePath: null,
        fileHandle: null,
        fileSourceMtimeMs: null,
        diskChangedWhileDirty: false,
        previewObjectUrl: null,
        tabColor: src.tabColor,
        dirty: false,
        fromShare: undefined,
        sessionRestorePlaceholder: undefined,
      };
    } else {
      clone = {
        ...src,
        id: this.newId(),
        title,
        filePath: null,
        fileHandle: null,
        savedContent: "",
        dirty: true,
        fileSourceMtimeMs: null,
        diskChangedWhileDirty: false,
        previewObjectUrl: null,
        history: [src.content],
        historyIndex: 0,
        fromShare: undefined,
        sessionRestorePlaceholder: undefined,
      };
    }
    this.docs.splice(index + 1, 0, clone);
    this.activeIdx = index + 1;
    return true;
  }

  /** New empty `Recording-*.max` tab (Max text buffer), made active — for toolbar hex video/mic. */
  addEmptyMaxRecordingTab(): void {
    const t = this.createEmptyMaxRecordingTab();
    this.docs.push(t);
    this.activeIdx = this.docs.length - 1;
  }

  /** Opens a new text tab from a GET `/inbox` payload (share server). */
  addTabFromShareInboxPayload(payload: { utf8: string; tabTitle: string | null }): void {
    const raw = payload.tabTitle?.trim();
    const baseName =
      raw && raw.length > 0 ? raw : `Shared-${this.untitledSeq++}`;
    const title = this.allocUniqueTabTitleAmongOpenTabs(baseName);
    const utf8 = payload.utf8;
    const tab = this.createTab(title, utf8, null, null);
    tab.fromShare = true;
    this.docs.push(tab);
    this.activeIdx = this.docs.length - 1;
  }

  /** If `desired` matches an existing tab title, append ` (2)`, ` (3)`, … before the last extension. */
  private allocUniqueTabTitleAmongOpenTabs(desired: string): string {
    const max = 512;
    const base = desired.trim().slice(0, max);
    const taken = new Set(this.docs.map((d) => d.title));
    if (!taken.has(base)) return base;
    const lastDot = base.lastIndexOf(".");
    const hasExt = lastDot > 0 && lastDot < base.length - 1;
    const stem = hasExt ? base.slice(0, lastDot) : base;
    const ext = hasExt ? base.slice(lastDot) : "";
    for (let n = 2; n < 100_000; n++) {
      const candidate = `${stem} (${n})${ext}`.slice(0, max);
      if (!taken.has(candidate)) return candidate;
    }
    return `${stem} (${Date.now()})${ext}`.slice(0, max);
  }

  setActiveIndex(i: number): void {
    if (i < 0 || i >= this.docs.length) return;
    this.activeIdx = i;
  }

  /** Advance tab group color (used from tab bar double-click). */
  cycleTabColor(index: number): void {
    const d = this.docs[index];
    if (!d) return;
    d.tabColor = nextTabColor(d.tabColor);
  }

  /** Set/clear exact tab group color (used from tab context menu). */
  setTabColor(index: number, color: TabColorId | null): void {
    const d = this.docs[index];
    if (!d) return;
    d.tabColor = color;
  }

  /** Remove group color from every tab (title bar “clear tab colors”). */
  clearAllTabColors(): void {
    for (const d of this.docs) {
      d.tabColor = null;
    }
  }

  /**
   * Concatenate all **text** tabs with the given group `color`, separated by `separator`,
   * into one new tab with **no** group color; removes the merged tabs (left-to-right order).
   */
  mergeTextTabsByColor(color: TabColorId, separator: string): MergeTextTabsByColorResult {
    const indices: number[] = [];
    for (let i = 0; i < this.docs.length; i++) {
      const d = this.docs[i]!;
      if (d.tabColor === color && d.viewMode === "text") indices.push(i);
    }
    if (indices.length < 2) {
      return {
        ok: false,
        error: "Need at least two text tabs with the selected color to merge.",
      };
    }
    const dirty = indices.filter((i) => this.docs[i]!.dirty);
    if (
      dirty.length > 0 &&
      !confirm(
        `Merge will close ${indices.length} tab(s) (${dirty.length} with unsaved changes). Continue?`,
      )
    ) {
      return { ok: false, error: "Cancelled." };
    }
    const parts = indices.map((i) => this.docs[i]!.content);
    const glue = `\n${separator}\n`;
    const merged = parts.join(glue);
    const title = `Merged (${color}) · ${indices.length} files`;
    const sortedDesc = [...indices].sort((a, b) => b - a);
    for (const idx of sortedDesc) {
      this.revokePreviewIfAny(this.docs[idx]!);
      this.docs.splice(idx, 1);
    }
    const nt = this.createTab(title, merged, null, null);
    // Merged output is a new synthesized document and should start unsaved.
    nt.dirty = true;
    this.docs.push(nt);
    this.activeIdx = this.docs.length - 1;
    return { ok: true };
  }

  /**
   * Split the **active** text tab on `\\n` + `separator` + `\\n` (same glue as merge). Replaces that
   * tab with N new text tabs titled `{stem}-{n}{ext}`; uses `newTabGroupColor` when set, else uncolored.
   */
  splitActiveTextBySeparator(separator: string): MergeTextTabsByColorResult {
    const idx = this.activeIdx;
    const d = this.docs[idx];
    if (!d || d.viewMode !== "text") {
      return { ok: false, error: "Active tab must be a text document." };
    }
    if (d.dirty && !confirm("Split this tab into several new tabs? Current buffer text is used.")) {
      return { ok: false, error: "Cancelled." };
    }
    const glue = `\n${separator}\n`;
    const parts = d.content.split(glue);
    if (parts.length < 2) {
      return {
        ok: false,
        error:
          "No split points found. Use the same separator as merge (newlines are added around it automatically).",
      };
    }
    const label = d.filePath ?? d.title;
    const { stem, ext } = this.splitTitleStemAndExt(label);
    this.revokePreviewIfAny(d);
    this.docs.splice(idx, 1);
    for (let p = 0; p < parts.length; p++) {
      const chunk = parts[p] ?? "";
      const title = `${stem}-${p + 1}${ext}`;
      const tab = this.createTab(title, chunk, null, null);
      // Split parts are newly created docs and should start unsaved.
      tab.dirty = true;
      if (this.newTabGroupColor !== null) {
        tab.tabColor = this.newTabGroupColor;
      }
      this.docs.splice(idx + p, 0, tab);
    }
    this.activeIdx = idx;
    return { ok: true };
  }

  /** Last path segment, then stem + extension for numbered split titles. */
  private splitTitleStemAndExt(displayPathOrTitle: string): { stem: string; ext: string } {
    const slash = Math.max(displayPathOrTitle.lastIndexOf("/"), displayPathOrTitle.lastIndexOf("\\"));
    const from = slash + 1;
    const base = displayPathOrTitle.slice(from);
    const dot = base.lastIndexOf(".");
    if (dot <= 0) return { stem: displayPathOrTitle, ext: "" };
    return {
      stem: displayPathOrTitle.slice(0, from + dot),
      ext: base.slice(dot),
    };
  }

  /**
   * Reorder tabs. `insertIndex` is the drop slot in the current left-to-right order (0 = before
   * first tab, `documents.length` = after last), from midpoint hit-testing.
   */
  moveTab(fromIndex: number, insertIndex: number): void {
    const n = this.docs.length;
    if (n <= 1) return;
    if (fromIndex < 0 || fromIndex >= n) return;
    if (insertIndex < 0 || insertIndex > n) return;
    const activeId = this.docs[this.activeIdx]?.id;
    const [tab] = this.docs.splice(fromIndex, 1);
    if (!tab) return;
    let dest = insertIndex;
    if (fromIndex < insertIndex) dest = insertIndex - 1;
    this.docs.splice(dest, 0, tab);
    const next = this.docs.findIndex((d) => d.id === activeId);
    this.activeIdx = next >= 0 ? next : 0;
  }

  /** Sync textarea value into active buffer and dirty flag. */
  syncFromEditor(text: string): void {
    const d = this.active;
    if (!d || d.viewMode !== "text") return;
    d.content = text;
    d.dirty = text !== d.savedContent;
  }

  /** Sync a textarea value into a specific text tab (multiview secondary pane). */
  syncFromEditorAt(tabIndex: number, text: string): void {
    const d = this.docs[tabIndex];
    if (!d || d.viewMode !== "text") return;
    d.content = text;
    d.dirty = text !== d.savedContent;
  }

  /** Call after user pauses typing (undo stack, §16.2). */
  checkpointHistory(text: string): void {
    const d = this.active;
    if (!d || d.viewMode !== "text") return;
    if (text === d.history[d.historyIndex]) return;
    d.history = d.history.slice(0, d.historyIndex + 1);
    d.history.push(text);
    d.historyIndex = d.history.length - 1;
    if (d.history.length > 150) {
      const drop = d.history.length - 150;
      d.history.splice(0, drop);
      d.historyIndex = Math.max(0, d.historyIndex - drop);
    }
  }

  undo(): string | null {
    const d = this.active;
    if (!d || d.viewMode !== "text" || d.historyIndex <= 0) return null;
    d.historyIndex -= 1;
    const t = d.history[d.historyIndex]!;
    d.content = t;
    d.dirty = t !== d.savedContent;
    return t;
  }

  redo(): string | null {
    const d = this.active;
    if (!d || d.viewMode !== "text" || d.historyIndex >= d.history.length - 1) return null;
    d.historyIndex += 1;
    const t = d.history[d.historyIndex]!;
    d.content = t;
    d.dirty = t !== d.savedContent;
    return t;
  }

  /** Replace active content from a command (EOL, open, etc.). */
  setActiveContent(text: string, recordHistory: boolean): void {
    const d = this.active;
    if (!d || d.viewMode !== "text") return;
    if (recordHistory) {
      d.history = d.history.slice(0, d.historyIndex + 1);
      d.history.push(d.content);
      d.history.push(text);
      d.historyIndex = d.history.length - 1;
    }
    d.content = text;
    d.dirty = text !== d.savedContent;
  }

  markSaved(
    text: string,
    title: string,
    path: string | null,
    handle: FileSystemFileHandle | null,
    fileSourceMtimeMs?: number | null,
  ): void {
    const d = this.active;
    if (!d || (d.viewMode !== "text" && d.viewMode !== "video")) return;
    d.content = text;
    d.savedContent = text;
    d.title = title;
    d.filePath = path;
    d.fileHandle = handle;
    d.dirty = false;
    d.diskChangedWhileDirty = false;
    d.history = [text];
    d.historyIndex = 0;
    d.language = inferLanguageModeFromFilename(path ?? title);
    if (fileSourceMtimeMs !== undefined) {
      d.fileSourceMtimeMs = fileSourceMtimeMs;
    } else if (!handle) {
      d.fileSourceMtimeMs = null;
    }
    const recentKey = path ?? title;
    this.pushRecent(recentKey);
    if (handle) void persistRecentFileHandle(recentKey, handle);
  }

  /** Re-read UTF-8 text from `fileHandle` into the tab at `index`. */
  async reloadTabFromDiskHandle(index: number): Promise<boolean> {
    const d = this.docs[index];
    if (!d?.fileHandle || (d.viewMode !== "text" && d.viewMode !== "video")) return false;
    let perm = await d.fileHandle.queryPermission({ mode: "read" });
    if (perm !== "granted") {
      perm = await d.fileHandle.requestPermission({ mode: "read" });
      if (perm !== "granted") return false;
    }
    let file: File;
    try {
      file = await d.fileHandle.getFile();
    } catch {
      return false;
    }
    const pathHint = d.filePath ?? d.title;
    if (d.viewMode === "text" && (await shouldTreatFileAsPdf(file, pathHint, d.fileHandle?.name ?? null))) {
      this.revokePreviewIfAny(d);
      const tab = this.createPdfTab(d.title, file, d.filePath, d.fileHandle);
      tab.id = d.id;
      tab.tabColor = d.tabColor;
      tab.fileSourceMtimeMs = file.lastModified;
      this.docs[index] = tab;
      return true;
    }
    if (d.viewMode === "text" && isImageViewFile(file)) {
      this.revokePreviewIfAny(d);
      const tab = this.createImageTab(d.title, file, d.filePath, d.fileHandle);
      tab.id = d.id;
      tab.tabColor = d.tabColor;
      tab.fileSourceMtimeMs = file.lastModified;
      this.docs[index] = tab;
      return true;
    }
    const text = await readFileAsText(file, pathHint, d.fileHandle?.name ?? null);
    this.revokePreviewIfAny(d);
    d.content = text;
    d.savedContent = text;
    d.dirty = false;
    d.diskChangedWhileDirty = false;
    d.history = [text];
    d.historyIndex = 0;
    d.fileSourceMtimeMs = file.lastModified;
    return true;
  }

  applyEol(style: EolStyle): void {
    const d = this.active;
    if (!d || d.viewMode !== "text") return;
    const next = convertEol(d.content, style);
    this.setActiveContent(next, true);
    d.eol = style;
  }

  setEncoding(enc: DocumentEncoding): void {
    const d = this.active;
    if (!d) return;
    d.encoding = enc;
  }

  async openIntoNewTab(): Promise<boolean> {
    const r = await openEditorFile();
    if (!r) return false;
    if (r.kind === "image") {
      const displayPath = await this.displayPathForFile(r.file, r.handle);
      const tab = this.createImageTab(r.name, r.file, displayPath, r.handle);
      tab.fileSourceMtimeMs = r.file.lastModified;
      this.docs.push(tab);
      this.activeIdx = this.docs.length - 1;
      this.pushRecent(displayPath);
      if (r.handle) void persistRecentFileHandle(displayPath, r.handle);
      return true;
    }
    if (r.kind === "pdf") {
      const displayPath = await this.displayPathForFile(r.file, r.handle);
      const tab = this.createPdfTab(r.name, r.file, displayPath, r.handle);
      tab.fileSourceMtimeMs = r.file.lastModified;
      this.docs.push(tab);
      this.activeIdx = this.docs.length - 1;
      this.pushRecent(displayPath);
      if (r.handle) void persistRecentFileHandle(displayPath, r.handle);
      return true;
    }
    const displayPath = await this.displayPathForFile(r.file, r.handle);
    const tab = this.createTab(r.name, r.text, displayPath, r.handle);
    if (r.handle) {
      try {
        tab.fileSourceMtimeMs = (await r.handle.getFile()).lastModified;
      } catch {
        /* leave null */
      }
    }
    this.docs.push(tab);
    this.activeIdx = this.docs.length - 1;
    this.pushRecent(displayPath);
    if (r.handle) void persistRecentFileHandle(displayPath, r.handle);
    return true;
  }

  /**
   * Replace a session-restore placeholder tab by opening the file picker again (same tab id / color).
   */
  async replaceActiveSessionPlaceholderFromPicker(): Promise<boolean> {
    const d = this.active;
    if (!d?.sessionRestorePlaceholder) return false;
    const want = d.sessionRestorePlaceholder;
    const r = await openEditorFile();
    if (!r) return false;
    const idx = this.activeIdx;
    if (want === "pdf") {
      const okPdf =
        r.kind === "pdf" ||
        (await shouldTreatFileAsPdf(r.file, r.file.name, r.handle?.name ?? null));
      if (!okPdf) {
        void alert("Please choose a PDF file.");
        return false;
      }
      const displayPath = await this.displayPathForFile(r.file, r.handle);
      const tab = this.createPdfTab(r.name, r.file, displayPath, r.handle);
      tab.id = d.id;
      tab.tabColor = d.tabColor;
      tab.fileSourceMtimeMs = r.file.lastModified;
      this.docs[idx] = tab;
      this.pushRecent(displayPath);
      if (r.handle) void persistRecentFileHandle(displayPath, r.handle);
      return true;
    }
    if (want === "image") {
      if (r.kind !== "image") {
        void alert("Please choose an image file.");
        return false;
      }
      const displayPath = await this.displayPathForFile(r.file, r.handle);
      const tab = this.createImageTab(r.name, r.file, displayPath, r.handle);
      tab.id = d.id;
      tab.tabColor = d.tabColor;
      tab.fileSourceMtimeMs = r.file.lastModified;
      this.docs[idx] = tab;
      this.pushRecent(displayPath);
      if (r.handle) void persistRecentFileHandle(displayPath, r.handle);
      return true;
    }
    return false;
  }

  /**
   * If the MRU list + IndexedDB has a handle for a path whose basename matches this tab’s title,
   * replace the session placeholder with that file (no picker). Tries MRU order.
   */
  async replaceActiveSessionPlaceholderFromRecentByName(): Promise<boolean> {
    const d = this.active;
    if (!d?.sessionRestorePlaceholder) return false;
    const want = d.sessionRestorePlaceholder;
    const title = d.title;
    const recent = DocumentManager.readRecent();
    const idx = this.activeIdx;
    for (const pathKey of recent) {
      if (basenameFromDisplayPath(pathKey) !== title) continue;
      const handle = await getRecentFileHandle(pathKey);
      if (!handle) continue;
      let perm = await handle.queryPermission({ mode: "read" });
      if (perm !== "granted") {
        perm = await handle.requestPermission({ mode: "read" });
      }
      if (perm !== "granted") continue;
      let file: File;
      try {
        file = await handle.getFile();
      } catch {
        continue;
      }
      if (want === "pdf" && !(await shouldTreatFileAsPdf(file, pathKey, handle.name))) continue;
      if (want === "image" && !isImageViewFile(file)) continue;

      if (want === "pdf") {
        const tabTitle = basenameFromDisplayPath(pathKey) || file.name;
        const tab = this.createPdfTab(tabTitle, file, pathKey, handle);
        tab.id = d.id;
        tab.tabColor = d.tabColor;
        tab.fileSourceMtimeMs = file.lastModified;
        this.docs[idx] = tab;
      } else if (want === "image") {
        const tab = this.createImageTab(file.name, file, pathKey, handle);
        tab.id = d.id;
        tab.tabColor = d.tabColor;
        tab.fileSourceMtimeMs = file.lastModified;
        this.docs[idx] = tab;
      } else {
        continue;
      }
      this.pushRecent(pathKey);
      void persistRecentFileHandle(pathKey, handle);
      return true;
    }
    return false;
  }

  /**
   * Open a file from a folder workspace (`rootFolderName` is the picked directory’s `.name`).
   * If a tab already exists for the same display path, activates it; **replaces** the tab when the
   * on-disk file should open as PDF/image but the existing buffer is text (or a session placeholder),
   * so re-clicking a `.pdf` after it was wrongly loaded as UTF-8 text restores the real preview.
   */
  async openFromWorkspace(
    rootFolderName: string,
    relativePath: string,
    fileHandle: FileSystemFileHandle,
  ): Promise<boolean> {
    const normalizedRel = relativePath.replace(/\\/g, "/");
    const displayPath = `${rootFolderName}/${normalizedRel}`;
    const slash = normalizedRel.lastIndexOf("/");
    const title = slash >= 0 ? normalizedRel.slice(slash + 1) : normalizedRel;

    let perm = await fileHandle.queryPermission({ mode: "readwrite" });
    if (perm !== "granted") {
      perm = await fileHandle.requestPermission({ mode: "readwrite" });
    }
    if (perm !== "granted") {
      let r = await fileHandle.queryPermission({ mode: "read" });
      if (r !== "granted") r = await fileHandle.requestPermission({ mode: "read" });
      if (r !== "granted") return false;
    }

    let file: File;
    try {
      file = await fileHandle.getFile();
    } catch {
      return false;
    }
    if (file.size > MAX_WORKSPACE_FILE_BYTES) return false;

    const asPdf = await shouldTreatFileAsPdf(file, displayPath, fileHandle.name);
    const existing = this.docs.findIndex((d) => d.filePath === displayPath);
    if (existing >= 0) {
      const d = this.docs[existing]!;
      if (asPdf) {
        this.revokePreviewIfAny(d);
        const tab = this.createPdfTab(title, file, displayPath, fileHandle);
        tab.id = d.id;
        tab.tabColor = d.tabColor;
        tab.fileSourceMtimeMs = file.lastModified;
        this.docs[existing] = tab;
        this.activeIdx = existing;
        this.pushRecent(displayPath);
        void persistRecentFileHandle(displayPath, fileHandle);
        return true;
      }
      if (isImageViewFile(file)) {
        this.revokePreviewIfAny(d);
        const tab = this.createImageTab(title, file, displayPath, fileHandle);
        tab.id = d.id;
        tab.tabColor = d.tabColor;
        tab.fileSourceMtimeMs = file.lastModified;
        this.docs[existing] = tab;
        this.activeIdx = existing;
        this.pushRecent(displayPath);
        void persistRecentFileHandle(displayPath, fileHandle);
        return true;
      }
      if (d.viewMode === "text" && !d.sessionRestorePlaceholder && !asPdf && !isImageViewFile(file)) {
        this.activeIdx = existing;
        return true;
      }
      this.revokePreviewIfAny(d);
      const text = await readFileAsText(file, displayPath, fileHandle.name);
      const tab = this.createTab(title, text, displayPath, fileHandle);
      tab.id = d.id;
      tab.tabColor = d.tabColor;
      tab.fileSourceMtimeMs = file.lastModified;
      this.docs[existing] = tab;
      this.activeIdx = existing;
      this.pushRecent(displayPath);
      void persistRecentFileHandle(displayPath, fileHandle);
      return true;
    }

    if (asPdf) {
      const tab = this.createPdfTab(title, file, displayPath, fileHandle);
      tab.fileSourceMtimeMs = file.lastModified;
      this.docs.push(tab);
      this.activeIdx = this.docs.length - 1;
      this.pushRecent(displayPath);
      void persistRecentFileHandle(displayPath, fileHandle);
      return true;
    }
    if (isImageViewFile(file)) {
      const tab = this.createImageTab(title, file, displayPath, fileHandle);
      tab.fileSourceMtimeMs = file.lastModified;
      this.docs.push(tab);
      this.activeIdx = this.docs.length - 1;
      this.pushRecent(displayPath);
      void persistRecentFileHandle(displayPath, fileHandle);
      return true;
    }

    const text = await readFileAsText(file, displayPath, fileHandle.name);
    const tab = this.createTab(title, text, displayPath, fileHandle);
    tab.fileSourceMtimeMs = file.lastModified;
    this.docs.push(tab);
    this.activeIdx = this.docs.length - 1;
    this.pushRecent(displayPath);
    void persistRecentFileHandle(displayPath, fileHandle);
    return true;
  }

  /**
   * Reopen a recent entry when a FileSystemFileHandle was stored for the same MRU key
   * (same session / browser profile).
   */
  async openRecentPath(pathKey: string): Promise<boolean> {
    const handle = await getRecentFileHandle(pathKey);
    if (!handle) return false;
    let perm = await handle.queryPermission({ mode: "read" });
    if (perm !== "granted") {
      perm = await handle.requestPermission({ mode: "read" });
      if (perm !== "granted") return false;
    }
    let file: File;
    try {
      file = await handle.getFile();
    } catch {
      return false;
    }
    if (await shouldTreatFileAsPdf(file, pathKey, handle.name)) {
      const tabTitle = basenameFromDisplayPath(pathKey) || file.name;
      const tab = this.createPdfTab(tabTitle, file, pathKey, handle);
      tab.fileSourceMtimeMs = file.lastModified;
      this.docs.push(tab);
      this.activeIdx = this.docs.length - 1;
      this.pushRecent(pathKey);
      void persistRecentFileHandle(pathKey, handle);
      return true;
    }
    if (isImageViewFile(file)) {
      const tab = this.createImageTab(file.name, file, pathKey, handle);
      tab.fileSourceMtimeMs = file.lastModified;
      this.docs.push(tab);
      this.activeIdx = this.docs.length - 1;
      this.pushRecent(pathKey);
      void persistRecentFileHandle(pathKey, handle);
      return true;
    }
    const text = await readFileAsText(file, pathKey, handle.name);
    const tab = this.createTab(file.name, text, pathKey, handle);
    tab.fileSourceMtimeMs = file.lastModified;
    this.docs.push(tab);
    this.activeIdx = this.docs.length - 1;
    this.pushRecent(pathKey);
    void persistRecentFileHandle(pathKey, handle);
    return true;
  }

  clearRecentFilesList(): void {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch {
      /* ignore */
    }
    void clearRecentFileHandles();
  }

  async saveActiveTab(): Promise<boolean> {
    const d = this.active;
    if (!d || (d.viewMode !== "text" && d.viewMode !== "video")) return false;
    const isNulla = d.viewMode === "video";
    const isExcel = !isNulla && d.language === "excel";
    const text = isNulla ? d.content : convertEol(d.content, d.eol);
    if (d.fileHandle) {
      try {
        if (isExcel) {
          const buf = await nppJsonStringToXlsxBuffer(d.content);
          await writeBinaryToExistingHandle(d.fileHandle, buf);
        } else {
          await writeToExistingHandle(d.fileHandle, text);
        }
        d.savedContent = d.content;
        d.dirty = false;
        d.diskChangedWhileDirty = false;
        try {
          d.fileSourceMtimeMs = (await d.fileHandle.getFile()).lastModified;
        } catch {
          /* keep previous mtime */
        }
        return true;
      } catch {
        /* fall through to save-as */
      }
    }
    const name = d.filePath?.split(/[/\\]/).pop() || d.title;
    if (isExcel) {
      const buf = await nppJsonStringToXlsxBuffer(d.content);
      const h = await saveExcelWithPicker(name, buf, null);
      if (h) {
        const file = await h.getFile();
        const pathKey = await this.displayPathForFile(file, h);
        this.markSaved(d.content, file.name, pathKey, h, file.lastModified);
        return true;
      }
      return false;
    }
    const h = await saveWithPicker(name, text, null);
    if (h) {
      const file = await h.getFile();
      const pathKey = await this.displayPathForFile(file, h);
      this.markSaved(d.content, file.name, pathKey, h, file.lastModified);
      return true;
    }
    return false;
  }

  async saveActiveTabAs(): Promise<boolean> {
    const d = this.active;
    if (!d || (d.viewMode !== "text" && d.viewMode !== "video")) return false;
    const isNulla = d.viewMode === "video";
    const isExcel = !isNulla && d.language === "excel";
    const text = isNulla ? d.content : convertEol(d.content, d.eol);
    const sugName = isNulla ? d.title : isExcel ? `${d.title.replace(/\.(xlsx|xlsm)$/i, "")}.xlsx` : `${d.title}.txt`;
    if (isExcel) {
      const buf = await nppJsonStringToXlsxBuffer(d.content);
      const h = await saveExcelWithPicker(sugName, buf, null);
      if (!h) return false;
      const file = await h.getFile();
      const pathKey = await this.displayPathForFile(file, h);
      this.markSaved(d.content, file.name, pathKey, h, file.lastModified);
      return true;
    }
    const h = await saveWithPicker(sugName, text, null);
    if (!h) return false;
    const file = await h.getFile();
    const pathKey = await this.displayPathForFile(file, h);
    this.markSaved(d.content, file.name, pathKey, h, file.lastModified);
    return true;
  }

  async saveAllTabs(): Promise<void> {
    const idx = this.activeIdx;
    for (let i = 0; i < this.docs.length; i++) {
      if (!this.docs[i]!.dirty) continue;
      this.activeIdx = i;
      await this.saveActiveTab();
    }
    this.activeIdx = idx;
  }

  private canDiscard(d: TabDocument): boolean {
    if (!d.dirty) return true;
    return confirm(`Close "${d.title}"? Unsaved changes will be lost.`);
  }

  /** Close tab at index (tab bar ×). */
  closeTabAt(index: number): boolean {
    const d = this.docs[index];
    if (!d || !this.canDiscard(d)) return false;
    this.revokePreviewIfAny(d);
    this.docs.splice(index, 1);
    if (this.activeIdx === index) {
      if (this.activeIdx >= this.docs.length) this.activeIdx = this.docs.length - 1;
    } else if (this.activeIdx > index) {
      this.activeIdx--;
    }
    if (!this.docs.length) {
      this.addEmptyTab();
      this.activeIdx = 0;
    }
    return true;
  }

  closeActiveTab(): boolean {
    return this.closeTabAt(this.activeIdx);
  }

  closeAllTabs(): void {
    const anyDirty = this.docs.some((d) => d.dirty);
    if (anyDirty && !confirm("Close all tabs? Unsaved changes may be lost.")) return;
    for (const doc of this.docs) {
      this.revokePreviewIfAny(doc);
    }
    this.docs = [];
    this.untitledSeq = 1;
    this.addEmptyTab();
  }

  private pushRecent(path: string): void {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      let list: string[] = raw ? JSON.parse(raw) : [];
      list = list.filter((p) => p !== path);
      list.unshift(path);
      if (list.length > MAX_RECENT_FILES) list.length = MAX_RECENT_FILES;
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch {
      /* ignore */
    }
  }

  static readRecent(): string[] {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }
}
